
import { Link } from 'react-router-dom';
import { useCart } from '../../hooks/useCart';

interface CartDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function CartDrawer({ isOpen, onClose }: CartDrawerProps) {
  const { cart, removeFromCart, updateQuantity, totalItems, totalPrice, clearCart } = useCart();

  return (
    <>
      {/* 오버레이 */}
      <div
        className={`fixed inset-0 bg-black/50 z-50 transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />

      {/* 장바구니 드로어 */}
      <div
        className={`fixed top-0 right-0 h-full w-full max-w-md bg-white z-50 shadow-2xl transform transition-transform duration-300 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* 헤더 */}
          <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-bold text-gray-900">장바구니</h2>
              {totalItems > 0 && (
                <span className="bg-gray-900 text-white text-xs px-2.5 py-1 rounded-full">
                  {totalItems}
                </span>
              )}
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-2xl text-gray-600" />
            </button>
          </div>

          {/* 장바구니 내용 */}
          <div className="flex-1 overflow-y-auto">
            {cart.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full px-6">
                <div className="w-20 h-20 bg-amber-50 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-shopping-bag-line text-4xl text-amber-400" />
                </div>
                <p className="text-gray-500 text-center mb-6">
                  장바구니가 비어있습니다
                </p>
                <button
                  onClick={onClose}
                  className="px-6 py-3 bg-gray-900 text-white rounded-full text-sm font-medium hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
                >
                  쇼핑 계속하기
                </button>
              </div>
            ) : (
              <div className="px-6 py-4 space-y-4">
                {cart.map((item) => (
                  <div
                    key={`${item.id}-${item.option}`}
                    className="flex gap-4 p-4 bg-gray-50 rounded-2xl"
                  >
                    <div className="w-20 h-20 bg-white rounded-xl overflow-hidden flex-shrink-0">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <h3 className="font-bold text-gray-900 truncate">{item.name}</h3>
                          <p className="text-sm text-gray-500">{item.option}</p>
                        </div>
                        <button
                          onClick={() => removeFromCart(item.id, item.option)}
                          className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
                        >
                          <i className="ri-delete-bin-line" />
                        </button>
                      </div>
                      <div className="flex items-center justify-between mt-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() =>
                              updateQuantity(item.id, item.option, item.quantity - 1)
                            }
                            className="w-8 h-8 bg-white rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors cursor-pointer"
                          >
                            <i className="ri-subtract-line text-gray-600" />
                          </button>
                          <span className="w-8 text-center font-medium text-gray-900">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() =>
                              updateQuantity(item.id, item.option, item.quantity + 1)
                            }
                            className="w-8 h-8 bg-white rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors cursor-pointer"
                          >
                            <i className="ri-add-line text-gray-600" />
                          </button>
                        </div>
                        <p className="font-bold text-gray-900">
                          {(item.price * item.quantity).toLocaleString()}원
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 하단 결제 영역 */}
          {cart.length > 0 && (
            <div className="border-t border-gray-100 px-6 py-5 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">총 상품 금액</span>
                <span className="text-2xl font-bold text-gray-900">
                  {totalPrice.toLocaleString()}원
                </span>
              </div>
              <button className="w-full py-4 bg-gray-900 text-white rounded-full font-medium hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer">
                주문하기
              </button>
              <button
                onClick={clearCart}
                className="w-full py-3 text-gray-500 text-sm hover:text-gray-700 transition-colors cursor-pointer"
              >
                장바구니 비우기
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
